<template>
  <b-container fluid>
    <page-title icon='fas fa-tachometer-alt' :countdown='selectedLocation ? countdown : null' :countdown-max='60'>
      <translate>Delivery Date Monitor</translate>
    </page-title>
    <b-row>
      <b-col>
        <b-card>
          <b-card-text>
            <b-row>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="destination" v-translate>Destination</label>
                  <b-form-select id='destination' v-model='selectedLocation' :options='locations' size='sm'
                    style="width: 90%;" @change='getMonitor'>
                    <template #first>
                      <option :value='null' disabled v-translate>Select a location</option>
                    </template>
                  </b-form-select>
                  <b-spinner small style="vertical-align: middle;" v-if="working" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="program" v-translate>Program</label>
                  <b-form-input id="program" type="text" v-model="programField" @change="getMonitor" />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="commodity-variety-remote" v-translate>Variety</label>
                  <commodity-variety-remote-autocomplete id="commodity-variety-remote" ref="commodityVarietyAutocomplete" @selected="setCommodityVariety" v-if="!commodityVariety.id" />
                  <div v-else>
                    <b-button size="sm" variant="danger" @click.stop="commodityVariety = {}; getMonitor();" style="padding: 2px 3px; line-height: 1">
                      <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 13px; vertical-align: middle" />
                    </b-button>
                    {{commodityVariety.external_id}} ({{commodityVariety.reference}})
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group>
                  <label for="delivery-date" v-translate>Delivery Date</label>
                  <b-form-input id="delivery-date" type="date" v-model="date" @change="getMonitor" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="selectedLocation && shippings.length">
      <b-col>
        <b-card>
          <b-row>
            <b-col cols="12" md="3">
              <doughnut
                v-if="selectedLocation && shippings.length"
                :chart-options="chartOptions"
                :chart-data="chartData"
                id="doughnut-chart"
                dataset-id-key="label"
                :plugins="chartHtmlLegendPlugin ? [chartHtmlLegendPlugin] : []"
                :width="300"
                :height="100"
                style="max-height: 200px;"
              />
            </b-col>
            <b-col class="align-self-center">
              <div id="legend-container"></div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="selectedLocation && shippings.length">
      <b-col id="shipping-cards">
        <div v-for="shipping in shippings.filter(x => hiddenStatuses.indexOf(x.status) === -1)" :key="shipping.id">
          <b-card v-if="shipping.distance || shipping.duration" class="mb-1 p-0">
          <b-row>
            <b-col>
              #{{ shipping.id | uuid }}
              <br />
              <b-button size="sm" @click.prevent v-b-modal="'modalShippingMap' + shipping.id" :disabled="!shipping.address_from || !shipping.address_to">
                <font-awesome-icon icon="map-marked-alt" size="2x" style="font-size: .9rem" />
              </b-button>
              <modal-shipping-map :shipping="shipping" update-every="30" without-button with-tracking />
            </b-col>
            <b-col>
              <dl class="mb-0">
                <dt v-translate>Order</dt>
                <dd class="mb-0">{{shipping.external_order_id}}</dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0" v-if="shipping.meta.geofences.from">
                <dt v-translate>Origin</dt>
                <dd class="mb-0">
                  <strong>
                    {{shipping.meta.geofences.from.name}}
                  </strong>
                  <div><small>({{shipping.meta.geofences.from.city}})</small></div>
                </dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0">
                <dt v-translate>Status</dt>
                <dd class="mb-0"><span :style="{color: statuses.find(s => s.label === shipping.status)?.color || '#000000'}">{{statusStrings[shipping.status] || shipping.status}}</span></dd>
              </dl>
            </b-col>
            <b-col>
              <dl class="mb-0" v-if="shipping.items?.[0]">
                <dt>{{ecosystemModules.items.label || this.$gettext('Items')}}</dt>
                <dd class="mb-0" v-b-tooltip :title="$gettextInterpolate($gettext('Last tracking sent on %{date}'), {date: moment.utc(shipping.items[0].item_tracking.last_position_updated_at).tz($tz).format('L LT')})"><span class="badge mr-1 badge-dark h6">{{shipping.items[0].reference_code}}</span></dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.distance">
              <dl class="mb-0">
                <dt v-translate>Distance</dt>
                <dd class="mb-0">{{distance(shipping.distance)}}</dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.duration">
              <dl class="mb-0">
                <dt v-translate :title="$gettext('Estimated Time of Completion')">E.T.C.</dt>
                <dd class="mb-0">{{moment.duration(shipping.duration, 'seconds').format('d [days] h [hrs] m [min]')}}</dd>
              </dl>
            </b-col>
            <b-col v-if="shipping.distance && shipping.duration">
              <dl class="mb-0">
                <dt v-translate :title="$gettext('Estimated Time of Arrival')">E.T.A.</dt>
                <dd v-show="shipping.eta" class="mb-0">
                  <span :title="moment.utc().add(shipping.eta, 'seconds').tz($tz).format('L LT')" v-b-tooltip>{{moment.duration(shipping.eta, 'seconds').format('d [days] h [hrs] m [min]')}}</span>
                  <span v-if="moment.duration(moment.utc().tz($tz) - moment.utc(shipping.etaUpdateDate).tz($tz)).asMinutes() > 10" class="text-danger" v-b-tooltip :title="$gettextInterpolate($gettext('Last tracking sent on %{date}'), {date: moment.utc(shipping.etaUpdateDate).tz($tz).format('L LT')})">(*)</span>
                </dd>
                <dd v-show="!shipping.eta" class="mb-0" v-translate>N/A</dd>
              </dl>
            </b-col>
            <b-col>
              <user-card v-if="shipping.courier" :user="shipping.courier" :profile-picture="false" card-style="micro" :with-map="false" without-id with-company></user-card>
            </b-col>
          </b-row>
        </b-card>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale
} from 'chart.js'
import constants from '@/common/constants'
import CommodityVarietyRemoteAutocomplete from '@/components/Autocompletes/CommodityVarietyRemoteAutocomplete'
import ModalShippingMap from '@/components/Maps/ModalShippingMap'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

export default {
  name: 'DeliveryDateMonitor',
  title() {
    return this.$gettext('Delivery Date Monitor')
  },
  components: {
    CommodityVarietyRemoteAutocomplete,
    ModalShippingMap,
    Doughnut
  },
  data() {
    return {
      intervalSummary: null,
      intervalCountdown: null,
      countdown: 60,
      working: false,
      selectedLocation: null,
      programField: '',
      commodityVariety: {},
      date: this.moment().format('YYYY-MM-DD'),
      locations: [
        {
          text: 'GALLO - LIVINGSTON WINERY (LIVINGSTON) - RC3.GMS-30',
          value: 'RC3.GMS-30'
        },
        {
          text: 'GALLO - TURNER ROAD VINTNERS (LODI) - RC3.GMS-775',
          value: 'RC3.GMS-775'
        },
        {
          text: 'GALLO - FRESNO WINERY (FRESNO) - RC3.GMS-20',
          value: 'RC3.GMS-20'
        },
        {
          text: 'GALLO - J VINEYARDS & WINERY (HEALDSBURG) - RC3.GMS-336',
          value: 'RC3.GMS-336'
        }
      ],
      statuses: [
        {
          label: 'INTRANSIT',
          color: '#01CCE0'
        },
        {
          label: 'ATPICKUPPOINT',
          color: '#3172F6'
        },
        {
          label: 'CONFIRMED',
          color: '#343a40'
        },
        {
          label: 'INCIDENCE',
          color: '#ffc107'
        },
        {
          label: 'READY',
          color: '#060'
        },
        {
          label: 'DRAFT',
          color: '#ccc'
        },
        {
          label: 'DELIVERED',
          color: '#28a745'
        }
      ],
      shippings: [],
      chartData: {
        labels: [],
        datasets: [
          {
            backgroundColor: [],
            data: []
          }
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        rotation: 0,
        borderJoinStyle: 'round',
        borderColor: '#ffffff',
        borderWidth: 2,
        borderRadius: 10,
        spacing: 0,
        weight: 100,
        cutout: '90%',
        plugins: {
          htmlLegend: {
            containerID: 'legend-container'
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            position: 'nearest',
            callbacks: {
              title: (context) => {
                return this.statusStrings[context[0].label] || context[0].label
              },
              label: (context) => {
                const value = context.parsed || 0
                const sum = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = sum ? (value / sum * 100).toFixed(2) : 0
                return `${value} (${percentage}%)`
              }
            }
          }
        }
      },
      chartHtmlLegendPlugin: this.htmlLegendPlugin(),
      hiddenStatuses: []
    }
  },
  mixins: [constants],
  mounted() {
    this.setTimeout()
  },
  beforeDestroy() {
    clearTimeout(this.intervalSummary)
    clearInterval(this.intervalCountdown)
  },
  methods: {
    setTimeout() {
      clearTimeout(this.intervalSummary)
      this.intervalSummary = setTimeout(() => {
        this.setTimeout()
      }, 60000)
      this.getMonitor()
    },
    setIntervalCountdown() {
      clearInterval(this.intervalCountdown)
      this.intervalCountdown = setInterval(function () {
        this.countdown = this.countdown === 0 ? 0 : this.countdown - 1
      }.bind(this), 1000)
    },
    getOrCreateLegendList(chart, id) {
      const legendContainer = document.getElementById(id)
      let listContainer = legendContainer.querySelector('ul')
      let totalContainer = document.createElement('div')

      if (!listContainer) {
        listContainer = document.createElement('ul')
        listContainer.className = 'd-flex d-md-block'
        listContainer.style.margin = '10px 0'
        listContainer.style.padding = 0

        legendContainer.appendChild(listContainer)

        totalContainer.id = 'total-container'
        totalContainer.className = 'text-center text-md-left'
        totalContainer.style.margin = 0
        totalContainer.style.padding = '0 40px'

        legendContainer.appendChild(totalContainer)
      }
      totalContainer = document.getElementById('total-container')
      totalContainer.innerHTML = this.$gettextInterpolate(this.$gettext('Total: <strong>%{total}</strong>'), {total: this.shippings.length})

      return listContainer
    },
    htmlLegendPlugin() {
      const that = this
      return {
        id: 'htmlLegend',
        afterUpdate(chart, args, options) {
          const ul = that.getOrCreateLegendList(chart, options.containerID)

          // Remove old legend items
          while (ul.firstChild) {
            ul.firstChild.remove()
          }

          // Reuse the built-in legendItems generator
          const items = chart.options.plugins.legend.labels.generateLabels(chart)

          items.forEach(item => {
            const li = document.createElement('li')
            li.style.alignItems = 'center'
            li.style.cursor = 'pointer'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.marginLeft = '10px'
            li.style.marginBottom = '10px'

            li.onclick = () => {
              const {type} = chart.config
              if (type === 'pie' || type === 'doughnut') {
                // Pie and doughnut charts only have a single dataset and visibility is per item
                chart.toggleDataVisibility(item.index)
                that.toggleDataVisibility(item.index)
              } else {
                chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
              }
              chart.update()
            }

            // Color box
            const boxSpan = document.createElement('span')
            boxSpan.style.background = item.fillStyle
            boxSpan.style.borderColor = item.strokeStyle
            boxSpan.style.borderWidth = item.lineWidth + 'px'
            boxSpan.style.display = 'inline-block'
            boxSpan.style.flexShrink = 0
            boxSpan.style.height = '12px'
            boxSpan.style.marginRight = '10px'
            boxSpan.style.width = '20px'

            // Text
            const textContainer = document.createElement('p')
            textContainer.style.color = item.fontColor
            textContainer.style.margin = 0
            textContainer.style.padding = 0
            textContainer.style.textDecoration = item.hidden ? 'line-through' : ''

            const text = document.createTextNode(item.text + ' (' + that.chartData.datasets[0].data[item.index] + ')')
            textContainer.appendChild(text)

            li.appendChild(boxSpan)
            li.appendChild(textContainer)
            ul.appendChild(li)
          })
        }
      }
    },
    setCommodityVariety(commodityVariety) {
      this.commodityVariety = commodityVariety
      this.getMonitor()
    },
    toggleDataVisibility(index) {
      if (this.hiddenStatuses.includes(this.statuses[index].label)) {
        this.hiddenStatuses = this.hiddenStatuses.filter(s => s !== this.statuses[index].label)
      } else {
        this.hiddenStatuses.push(this.statuses[index].label)
      }
    },
    getMonitor() {
      if (this.selectedLocation) {
        this.working = true
        this.countdown = 60
        this.setIntervalCountdown()

        const params = {
          page: 0,
          filter_groups: [{
            filters: [
              ['delivery_date', 'bt', [
                this.moment(this.date).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
                this.moment(this.date).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
              ]],
              ['destination_location', 'eq', this.selectedLocation],
              ['status', 'in', ['draft', 'pending', 'canceled'], true]
            ]
          }],
          includes: ['shippings.courier.courier_available.tracking', 'shippings.items.item_tracking', 'shippings.status_history']
        }
        if (this.programField) {
          params.filter_groups[0].filters.push(['program_field', 'eq', this.programField])
        }
        if (this.commodityVariety.id) {
          params.filter_groups[0].filters.push(['commodity_variety_id', 'eq', this.commodityVariety.id])
        }

        this.$http.get(this.$appConfig.api.baseUrl + 'shipping-orders', {params})
          .then(response => {
            let stuff = {}
            this.shippings = []
            this.statuses.forEach(status => {
              stuff[status.label] = []
              stuff[status.label] = [...stuff[status.label], ...response.body.data.map(order => order.shippings.filter(shipping => shipping.status === status.label)).filter(arr => arr.length > 0).map(arr => arr[0])]
              this.shippings = [...this.shippings, ...stuff[status.label]]
            })

            this.chartData.labels = this.statuses.map(status => this.statusStrings[status.label] || status.label)
            this.chartData.datasets[0].backgroundColor = this.statuses.map(status => status.color)
            this.chartData.datasets[0].data = this.statuses.map(status => stuff[status.label].length)

            this.shippings = this.shippings.map(shipping => {
              shipping.eta = null
              if (shipping.status === 'INTRANSIT') {
                const courierTrackingDate = shipping.courier.courier_available?.tracking?.[0].created_at || 0
                const itemTrackingDate = shipping.items?.[0]?.item_tracking.last_position_updated_at || 0
                const trackingToUse = itemTrackingDate > courierTrackingDate ? shipping.items[0].item_tracking : shipping.courier.courier_available.tracking[0]
                console.log(shipping.items?.[0], courierTrackingDate, itemTrackingDate, itemTrackingDate > courierTrackingDate ? 'shipping.items[0].item_tracking' : 'shipping.courier.courier_available.tracking[0]')

                const d2 = this.getDistance({lat: trackingToUse.lat, lng: trackingToUse.lng}, {lat: shipping.address_to.lat, lng: shipping.address_to.lng})

                const td = shipping.duration / shipping.distance

                shipping.eta = td * (d2 * 1.2)
                shipping.etaType = itemTrackingDate > courierTrackingDate ? 'item' : 'courier'
                shipping.etaUpdateDate = trackingToUse.last_position_updated_at || trackingToUse.updated_at || trackingToUse.created_at
              }
              return shipping
            })
            this.shippings = this.shippings.sort((a, b) => {
              if (a.eta === null && b.eta === null) return 0
              if (a.eta === null) return 1
              if (b.eta === null) return -1
              return a.eta - b.eta
            })
            this.working = false
          })
      }
    }
  }
}
</script>

<style lang='scss'>
.canvasjs-chart-credit {
  display: none !important
}
#shipping-cards {
  .card-body {
    padding: 10px 15px;
  }
}
</style>
